import { headerLinks as legacyHeaderLinks } from '@fe-toolkit/utility'
import { blackFridayHeaderLinks, blackFridayPreRegistrationHeaderLinks } from '@giffgaff/fe-design-system-content'

import type { Header as LegacyHeader } from '@fe-toolkit/web-components/ssr'
import type { ComponentProps } from 'react'

import type { FooterSocialLinks } from '@giffgaff/fe-design-system-ui'

/**
 * Copy for the fallback chrome. The header and footer navigation themselves are feds-ui's
 * packaged defaults (`@giffgaff/fe-design-system-content`); only what that package does not
 * carry lives here.
 */

// The legacy Header keys links by `id`; these stay clear of the toolkit's ids (as fe-common's do).
const LEGACY_BLACK_FRIDAY_ID = 1000
const LEGACY_PRE_REGISTRATION_ID = 1001

type LegacyHeaderTopLink = NonNullable<ComponentProps<typeof LegacyHeader>['links']>[number]
type PackagedLink = (typeof blackFridayHeaderLinks)[number] | (typeof blackFridayPreRegistrationHeaderLinks)[number]

/**
 * The packaged entry with this `entryId`, in the legacy Header's link shape: the same conversion as
 * fe-common's CommonHeader/navigation.ts, so the fallback's legacy Black Friday links match fe-common's.
 */
const toLegacyLink = (links: readonly PackagedLink[], entryId: string, id: number): LegacyHeaderTopLink => {
  const entry = links.find((link) => link.entryId === entryId)

  if (!entry) throw new Error(`@giffgaff/fe-design-system-content has no header link '${entryId}'`)

  const { title, loggedInOnly, loggedOutOnly } = entry
  const url = 'url' in entry ? entry.url : undefined
  const analyticsId = 'analyticsId' in entry ? entry.analyticsId : undefined
  const subLinks = 'sub_links' in entry ? entry.sub_links : undefined

  return {
    id,
    title,
    ...(url ? { url } : {}),
    ...(analyticsId ? { analyticsId } : {}),
    loggedInOnly,
    loggedOutOnly,
    openByDefaultMobile: false,
    ...(subLinks
      ? {
          sub_links: subLinks.map((sub) => ({
            title: sub.title,
            url: sub.url,
            analyticsId: sub.analyticsId,
            loggedInOnly: sub.loggedInOnly,
            loggedOutOnly: sub.loggedOutOnly,
          })),
        }
      : {}),
  }
}

/** The legacy navigation with the packaged Black Friday category first, as fe-common's legacy header has it. */
export const LEGACY_BLACK_FRIDAY_HEADER_LINKS: LegacyHeaderTopLink[] = [
  toLegacyLink(blackFridayHeaderLinks, 'black-friday', LEGACY_BLACK_FRIDAY_ID),
  ...legacyHeaderLinks,
]

/**
 * The legacy navigation with the packaged pre-registration link first, as fe-common's legacy header has it.
 * From content 0.7.0 pre-registration shows the same Black Friday link, keyed `black-friday`.
 */
export const LEGACY_BLACK_FRIDAY_PREREG_HEADER_LINKS: LegacyHeaderTopLink[] = [
  toLegacyLink(blackFridayPreRegistrationHeaderLinks, 'black-friday', LEGACY_PRE_REGISTRATION_ID),
  ...legacyHeaderLinks,
]

/** feds-content's pre-registration link, for tests. */
export const BLACK_FRIDAY_PREREG_LINK = blackFridayPreRegistrationHeaderLinks.find(
  (link) => link.entryId === 'black-friday'
)!

/** fe-common's member avatar, shown beside the signed-in member's name. */
export const ACCOUNT_IMAGE_URL = 'https://giffgaff.com/styleguide/svg/gamification/avatar_default.svg'

export const FOOTER_SOCIAL_LINKS = {
  instagram: { label: 'Instagram', href: 'https://www.instagram.com/giffgaff/' },
  facebook: { label: 'Facebook', href: 'https://www.facebook.com/giffgaff' },
  linkedIn: { label: 'LinkedIn', href: 'https://www.linkedin.com/company/giffgaff/' },
  youtube: { label: 'YouTube', href: 'https://www.youtube.com/giffgaff' },
} as const satisfies FooterSocialLinks
